import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, combineLatest, map, switchMap, timer } from 'rxjs';

import { ENV_CONFIG } from '../../../core/env-config';
import { LanguageService } from '../../../core/i18n/language.service';
import { CurrentWeather, OpenWeatherResponse, toCurrentWeather } from './weather.models';
import { ContextService } from '../../../core/context.service';

const API_URL = 'https://api.openweathermap.org/data/2.5/weather';
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const DEFAULT_CITY = 'London,GB';

/**
 * Polls OpenWeather for the topbar weather chip. The API key lives in the env
 * config (not the core API, so the auth interceptor leaves these requests
 * alone); without a key the integration is disabled and the chip is hidden.
 *
 * Refetches on language switch because OpenWeather localizes `description`
 * via its `lang` param. A failed poll keeps the last good reading and retries
 * on the next tick.
 */
@Injectable({ providedIn: 'root' })
export class WeatherService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENV_CONFIG);
  private readonly languageService = inject(LanguageService);
  private readonly contextService = inject(ContextService);

  readonly enabled = !!this.env.weatherApiKey;

  readonly current = signal<CurrentWeather | null>(null);
  /** Epoch ms of the last successful fetch; feeds "updated N min ago". */
  readonly updatedAt = signal<number | null>(null);
  readonly failed = signal(false);

  constructor() {
    if (!this.enabled) {
      return;
    }
    combineLatest([
      timer(0, REFRESH_INTERVAL_MS),
      toObservable(this.languageService.current),
      this.contextService.weatherLocation$,
    ]).pipe(
        switchMap(([, lang, city]) =>
          this.fetch(lang, city ?? DEFAULT_CITY).pipe(
            catchError(() => {
              this.failed.set(true);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe((weather) => {
        this.current.set(weather);
        this.updatedAt.set(Date.now());
        this.failed.set(false);
      });
  }

  private fetch(lang: string, city: string): Observable<CurrentWeather> {
    return this.http
      .get<OpenWeatherResponse>(API_URL, {
        params: {
          q: city,
          units: 'metric', // °C and m/s in; UnitService renders the user's preference
          lang,
          appid: this.env.weatherApiKey!,
        },
      })
      .pipe(map(toCurrentWeather));
  }
}
